'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Globe, Lock, User, Users } from 'lucide-react';
import * as React from 'react';
import { cn } from '../../../lib/utils';
import { SpecraBadge } from '../ensigns/badge';
import { SpecraButton } from '../ensigns/button';
import { SpecraTag } from '../ensigns/tag';
import { SpecraHeading, SpecraText } from '../ensigns/typography';
import { SpecraCombobox } from '../lieutenants/combobox';
import { SpecraEmptyState } from '../lieutenants/empty-state';
import { SpecraInlineBanner } from '../lieutenants/inline-banner';
import { SpecraPopover } from '../lieutenants/popover';
import { SpecraActionList } from './action-list';
import { SpecraPersonPicker } from './person-picker';
const DEFAULT_LABELS = {
    onlyMe: 'Only me',
    team: 'My team',
    people: 'Specific people',
    everyone: 'Everyone who can see intel',
    save: 'Save',
    cancel: 'Cancel',
    narrowingNote: 'People who already read it may remember it.'
};
const DESCRIPTIONS = {
    personal: 'Just you. Start here.',
    team: 'Everyone in a team or group you pick.',
    people: 'Only the people you name.',
    everyone: 'Anyone your workspace lets read intel.'
};
const ONLY_ME = {
    everyone: false,
    targets: []
};
const isTeam = (t)=>t.kind !== 'USER';
const keyOf = (t)=>`${t.kind}:${t.id}`;
/** The level a value reads as: everyone > any named person > teams only > only me. */ export function audienceLevelOf(value) {
    if (value.everyone) return 'everyone';
    if (value.targets.length === 0) return 'personal';
    return value.targets.some((t)=>t.kind === 'USER') ? 'people' : 'team';
}
function listLabels(labels) {
    if (labels.length <= 1) return labels.join('');
    if (labels.length <= 3) return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`;
    const more = labels.length - 2;
    return `${labels.slice(0, 2).join(', ')} and ${more} others`;
}
/** Default live sentence. */ export function describeAudience(value, everyoneLabel) {
    if (value.everyone) return everyoneLabel ?? DEFAULT_LABELS.everyone;
    if (value.targets.length === 0) return 'Only you';
    return `You and ${listLabels(value.targets.map((t)=>t.label))}`;
}
function sameAudience(a, b) {
    if (a.everyone !== b.everyone) return false;
    if (a.everyone) return true;
    if (a.targets.length !== b.targets.length) return false;
    const keys = new Set(a.targets.map(keyOf));
    return b.targets.every((t)=>keys.has(keyOf(t)));
}
/** True when someone who could see `initial` can no longer see `next`. */ export function isNarrowerAudience(initial, next) {
    if (next.everyone) return false;
    if (initial.everyone) return true;
    const keys = new Set(next.targets.map(keyOf));
    return initial.targets.some((t)=>!keys.has(keyOf(t)));
}
/** "Who can see this": Only me → My team → Specific people → Everyone who can see intel. */ export function SpecraAudiencePicker(props) {
    const { anchor, mode, onOpenChange, open = false, title = 'Who can see this' } = props;
    if (mode === 'inline') return /*#__PURE__*/ _jsx(AudienceEditor, {
        ...props,
        active: true
    });
    return /*#__PURE__*/ _jsx(SpecraPopover, {
        anchor: anchor ?? null,
        "aria-label": title,
        "data-testid": props['data-testid'],
        onOpenChange: (next)=>onOpenChange?.(next),
        open: open,
        children: /*#__PURE__*/ _jsx(AudienceEditor, {
            ...props,
            active: open,
            "data-testid": undefined
        })
    });
}
SpecraAudiencePicker.displayName = 'SpecraAudiencePicker';
function AudienceEditor({ active, className, describe, disabled = false, hideEveryone = false, labels: labelOverrides, mode, onChange, onOpenChange, onPeopleQueryChange, onPeopleRetry, onTeamQueryChange, onTeamsRetry, peopleError = false, peopleLoading = false, peopleOptions, peopleQuery, saving = false, suggestedTeam, teamOptions, teamQuery, teamsError = false, teamsLoading = false, title = 'Who can see this', value, 'data-testid': testId }) {
    const labels = {
        ...DEFAULT_LABELS,
        ...labelOverrides
    };
    const inline = mode === 'inline';
    const [draft, setDraft] = React.useState(value);
    const [step, setStep] = React.useState(()=>audienceLevelOf(value));
    const [focusStep, setFocusStep] = React.useState(step);
    const initialRef = React.useRef(value);
    const groupRef = React.useRef(null);
    const headingId = React.useId();
    // Popover: every open starts from the saved value and puts focus on the
    // checked choice (SpecraPopover does not auto-focus).
    // biome-ignore lint/correctness/useExhaustiveDependencies: reset only when the popover opens.
    React.useEffect(()=>{
        if (inline || !active) return;
        const level = audienceLevelOf(value);
        initialRef.current = value;
        setDraft(value);
        setStep(level);
        setFocusStep(level);
        const id = window.setTimeout(()=>{
            groupRef.current?.querySelector('[aria-checked="true"]')?.focus();
        }, 0);
        return ()=>window.clearTimeout(id);
    }, [
        active,
        inline
    ]);
    // Inline: the parent owns the value.
    const current = inline ? value : draft;
    const update = (next)=>{
        if (inline) onChange(next);
        else setDraft(next);
    };
    const choose = (level)=>{
        setStep(level);
        setFocusStep(level);
        if (level === 'personal') update(ONLY_ME);
        else if (level === 'everyone') update({
            everyone: true,
            targets: []
        });
        else if (level === 'team') {
            const teams = current.targets.filter(isTeam);
            if (teams.length > 0) update({
                everyone: false,
                targets: teams
            });
            else if (suggestedTeam) update({
                everyone: false,
                targets: [
                    suggestedTeam
                ]
            });
            else if (current.everyone) update(ONLY_ME);
        } else update({
            everyone: false,
            targets: current.everyone ? [] : current.targets
        });
    };
    const addTarget = (target)=>{
        if (current.targets.some((t)=>keyOf(t) === keyOf(target))) return;
        // The team step holds teams only; the people step keeps teams and people.
        const kept = step === 'team' ? current.targets.filter(isTeam) : current.targets;
        update({
            everyone: false,
            targets: [
                ...kept,
                target
            ]
        });
    };
    const removeTarget = (target)=>{
        update({
            everyone: false,
            targets: current.targets.filter((t)=>keyOf(t) !== keyOf(target))
        });
    };
    const levels = hideEveryone ? [
        'personal',
        'team',
        'people'
    ] : [
        'personal',
        'team',
        'people',
        'everyone'
    ];
    const ICONS = {
        personal: Lock,
        team: Users,
        people: User,
        everyone: Globe
    };
    const LABELS = {
        personal: labels.onlyMe,
        team: labels.team,
        people: labels.people,
        everyone: labels.everyone
    };
    const items = levels.map((level)=>{
        const Icon = ICONS[level];
        return {
            id: level,
            icon: /*#__PURE__*/ _jsx(Icon, {
                "aria-hidden": "true",
                className: "size-4"
            }),
            label: LABELS[level],
            description: DESCRIPTIONS[level],
            onClick: disabled ? undefined : ()=>choose(level),
            selected: step === level
        };
    });
    const moveFocus = (from, delta)=>{
        const index = (levels.indexOf(from) + delta + levels.length) % levels.length;
        const next = levels[index];
        setFocusStep(next);
        groupRef.current?.querySelector(`[data-item-id="${next}"]`)?.focus();
    };
    const sentence = describe ? describe(current) : describeAudience(current, labels.everyone);
    const changed = !sameAudience(initialRef.current, current);
    const narrower = changed && isNarrowerAudience(initialRef.current, current);
    const chosenTeams = current.targets.filter(isTeam);
    const tagsFor = (targets)=>targets.length > 0 ? /*#__PURE__*/ _jsx("ul", {
            "aria-label": "Chosen",
            className: "m-0 flex list-none flex-wrap gap-[var(--space-1)] p-0",
            children: targets.map((t)=>/*#__PURE__*/ _jsx("li", {
                    children: /*#__PURE__*/ _jsx(SpecraTag, {
                        onRemove: disabled ? undefined : ()=>removeTarget(t),
                        removeLabel: `Remove ${t.label}`,
                        tone: isTeam(t) ? 'info' : 'neutral',
                        children: t.label
                    })
                }, keyOf(t)))
        }) : null;
    const chosenKeys = new Set(current.targets.map(keyOf));
    const teamList = suggestedTeam ? [
        suggestedTeam,
        ...teamOptions.filter((t)=>keyOf(t) !== keyOf(suggestedTeam))
    ] : teamOptions;
    const teamComboOptions = teamList.filter((t)=>!chosenKeys.has(keyOf(t))).map((t)=>({
            value: keyOf(t),
            label: t.label,
            textValue: t.label,
            description: suggestedTeam && keyOf(t) === keyOf(suggestedTeam) ? /*#__PURE__*/ _jsxs("span", {
                className: "inline-flex items-center gap-[var(--space-1)]",
                children: [
                    /*#__PURE__*/ _jsx(SpecraBadge, {
                        className: "py-0",
                        variant: "info",
                        children: "Suggested"
                    }),
                    t.description
                ]
            }) : t.description,
            keywords: [
                t.label,
                ...t.description ? [
                    t.description
                ] : []
            ]
        }));
    const teamByKey = new Map(teamList.map((t)=>[
            keyOf(t),
            t
        ]));
    const people = peopleOptions.filter((p)=>!chosenKeys.has(keyOf(p))).map((p)=>({
            id: keyOf(p),
            name: p.label,
            description: p.description
        }));
    const personByKey = new Map(peopleOptions.map((p)=>[
            keyOf(p),
            p
        ]));
    const errorBanner = (message, retry)=>/*#__PURE__*/ _jsx(SpecraInlineBanner, {
            action: retry ? /*#__PURE__*/ _jsx(SpecraButton, {
                onClick: retry,
                size: "xs",
                variant: "secondary",
                children: "Try again"
            }) : undefined,
            tone: "danger",
            children: message
        });
    const teamsEmpty = teamList.length === 0 && !teamsLoading && !teamsError && !teamQuery;
    const teamSection = step === 'team' ? /*#__PURE__*/ _jsxs("div", {
        className: "flex flex-col gap-[var(--space-2)]",
        "data-audience-section": "team",
        children: [
            teamsError ? errorBanner('Could not load your teams.', onTeamsRetry) : null,
            teamsEmpty ? /*#__PURE__*/ _jsx(SpecraEmptyState, {
                size: "compact",
                title: "No teams you belong to yet."
            }) : /*#__PURE__*/ _jsx(SpecraCombobox, {
                disabled: disabled,
                emptyMessage: "No teams match.",
                label: "Add a team or group",
                loading: teamsLoading,
                onSearchValueChange: onTeamQueryChange,
                onValueChange: (key)=>{
                    const team = key ? teamByKey.get(key) : undefined;
                    if (team) addTarget(team);
                },
                options: teamComboOptions,
                placeholder: "Add a team or group…",
                searchPlaceholder: "Search teams…",
                searchValue: teamQuery,
                shouldFilter: !onTeamQueryChange,
                value: ""
            }),
            tagsFor(chosenTeams)
        ]
    }) : null;
    const peopleSection = step === 'people' ? /*#__PURE__*/ _jsxs("div", {
        className: "flex flex-col gap-[var(--space-2)]",
        "data-audience-section": "people",
        children: [
            peopleError ? errorBanner('Could not search people.', onPeopleRetry) : null,
            /*#__PURE__*/ _jsx(SpecraPersonPicker, {
                disabled: disabled,
                emptyMessage: peopleLoading ? 'Searching…' : 'Type a name to find people.',
                label: "Add a person",
                loading: peopleLoading,
                onSearchValueChange: onPeopleQueryChange,
                onValueChange: (key)=>{
                    const person = key ? personByKey.get(key) : undefined;
                    if (person) addTarget(person);
                },
                people: people,
                placeholder: "Add a person…",
                searchValue: peopleQuery,
                shouldFilter: !onPeopleQueryChange,
                value: ""
            }),
            people.length === 0 && !peopleQuery && !peopleLoading && !peopleError ? /*#__PURE__*/ _jsx(SpecraEmptyState, {
                className: "px-0 py-0",
                size: "compact",
                title: "Type a name to find people."
            }) : null,
            tagsFor(current.targets)
        ]
    }) : null;
    return /*#__PURE__*/ _jsxs("div", {
        className: cn('flex flex-col gap-[var(--space-3)]', className),
        "data-audience-mode": mode,
        "data-testid": testId,
        children: [
            inline ? null : /*#__PURE__*/ _jsx(SpecraHeading, {
                id: headingId,
                level: 3,
                size: "ui",
                children: title
            }),
            /*#__PURE__*/ _jsx(SpecraActionList, {
                "aria-label": inline ? title : undefined,
                "aria-labelledby": inline ? undefined : headingId,
                getItemProps: (item)=>{
                    const level = item.id;
                    return {
                        role: 'radio',
                        'aria-checked': step === level,
                        'aria-current': undefined,
                        'aria-disabled': disabled || undefined,
                        tabIndex: focusStep === level ? 0 : -1,
                        onFocus: ()=>setFocusStep(level),
                        onKeyDown: (event)=>{
                            if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
                                event.preventDefault();
                                moveFocus(level, 1);
                            } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
                                event.preventDefault();
                                moveFocus(level, -1);
                            }
                        }
                    };
                },
                items: items,
                ref: groupRef,
                role: "radiogroup",
                trailing: null
            }),
            teamSection,
            peopleSection,
            /*#__PURE__*/ _jsx(SpecraText, {
                "aria-live": "polite",
                as: "p",
                className: "m-0",
                size: "meta",
                tone: "muted",
                children: sentence
            }),
            narrower ? /*#__PURE__*/ _jsx(SpecraInlineBanner, {
                tone: "info",
                children: labels.narrowingNote
            }) : null,
            inline ? null : /*#__PURE__*/ _jsxs("div", {
                className: "flex justify-end gap-[var(--space-2)]",
                children: [
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        disabled: saving,
                        onClick: ()=>onOpenChange?.(false),
                        size: "sm",
                        variant: "ghost",
                        children: labels.cancel
                    }),
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        disabled: disabled || !changed,
                        isLoading: saving,
                        onClick: ()=>onChange(current),
                        size: "sm",
                        variant: "default",
                        children: labels.save
                    })
                ]
            })
        ]
    });
}
